import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { askAssistant, askAssistantStream } from "./api";
import { getRecentQuestions, pushRecentQuestion } from "./recentQuestions";

// useAssistant owns the panel open-state and the in-session thread. Navigation
// intents with auto=true route immediately without opening the panel.
export function useAssistant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]); // [{ id, question, response }]
  const [conversationId, setConversationId] = useState(null);
  const [recentQuestions, setRecentQuestions] = useState(getRecentQuestions);
  const nextId = useRef(0);
  const inFlight = useRef(false);

  const onNavigate = useCallback((navOrAction) => {
    if (navOrAction?.path) {
      navigate(navOrAction.path);
    }
  }, [navigate]);

  const ask = useCallback(async (question, context = {}) => {
    const q = (question || "").trim();
    if (!q) return;
    // Guard against overlapping asks (e.g. a rapid follow-up or chip click
    // firing while a prior ask is still in flight) — a ref is used instead
    // of the `busy` state because state may be stale inside a rapid closure.
    if (inFlight.current) return;
    inFlight.current = true;
    // Record the question for the ask box's "Recent" list (newest-first).
    pushRecentQuestion(q);
    setRecentQuestions(getRecentQuestions());
    const id = nextId.current++;
    setBusy(true);
    // Open the panel immediately and show the question with a pending slot, so
    // the drawer slides in the instant the question is asked and renders a
    // "thinking" state while the answer is generated — rather than waiting for
    // the response to arrive. An auto-navigation (below) closes it right back.
    setThread((prev) => [...prev, { id, question: q, response: null }]);
    setOpen(true);

    // Prefer the SSE stream (staged progress + earlier first paint); on ANY
    // stream failure (rejected fetch, unsupported body stream, no `answer`
    // frame ever arriving, etc.) fall back to the plain sync call so the
    // user always gets an answer. This is the safety net the task calls for.
    const fetchResponse = async () => {
      let streamAnswer = null;
      let streamConversationId = null;
      try {
        await askAssistantStream(q, context, conversationId, {
          onStage: (stage) => {
            setThread((prev) => prev.map((t) => (t.id === id ? { ...t, stage } : t)));
          },
          onAnswer: (resp) => {
            streamAnswer = resp;
          },
          onDone: ({ conversation_id }) => {
            streamConversationId = conversation_id;
          },
        });
        if (!streamAnswer) {
          throw new Error("Stream ended without an answer");
        }
        return { ...streamAnswer, _conversationId: streamConversationId ?? streamAnswer._conversationId };
      } catch (_streamErr) {
        if (streamAnswer) {
          // The answer already arrived over the stream; a later stream error (e.g. a
          // dropped `done` frame) must NOT trigger a second sync round-trip that would
          // double-persist this turn on the backend. Return what the stream already gave us.
          return { ...streamAnswer, _conversationId: streamConversationId ?? streamAnswer._conversationId };
        }
        return askAssistant(q, context, conversationId);
      }
    };

    try {
      const response = await fetchResponse();
      if (response._conversationId) {
        setConversationId(response._conversationId);
      }
      // Auto-navigate high-confidence navigation: close the panel we opened
      // optimistically (it slides back out) and route to the target instead.
      if (response.intent === "navigate" && response.navigation?.auto) {
        setOpen(false);
        onNavigate(response.navigation);
        setThread((prev) => prev.filter((t) => t.id !== id)); // drop the pending slot
        return;
      }
      setThread((prev) => prev.map((t) => (t.id === id ? { ...t, response, stage: undefined } : t)));
    } catch (e) {
      setOpen(true);
      setThread((prev) => prev.map((t) => (t.id === id ? { ...t, error: e?.message || "Something went wrong", stage: undefined } : t)));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [onNavigate, conversationId]);

  const close = useCallback(() => setOpen(false), []);
  return { open, busy, thread, ask, close, onNavigate, conversationId, recentQuestions };
}
